import { get } from 'svelte/store';
import type { Device } from 'src/types/Device';
import type { DeviceInfoG2 } from 'src/types/DeviceInfoG2';
import type { LocalRequest } from 'src/types/requests/local';
import { SLEEPING_TYPES, HALF_SLEEPING_TYPES } from '../constants';
import { home_data } from 'src/stores/account/user';
import { devices_settings, devices_status } from 'src/stores/device-runtime/status';
import { devices } from 'src/stores/lists/devices';
import { wifi_ssid } from 'src/stores/session/native-bridge';
import { allDiscovered } from 'src/stores/features/discovered';
import {
	clear_verified_local_ips,
	get_fresh_verified_local_ip,
	set_verified_local_ip,
} from 'src/stores/device-runtime/verified-local-ips';
import { validate_ip } from './validators';
import { get_local_ip, get_ssid, is_gen_2, real_id } from './interface_helpers';
import { get_local_device_info } from '../services/local_requests';

/** HTTP timeout for a full multi-interface background scan. */
export const LOCAL_IP_PROBE_TIMEOUT_MS = 3000;

/** HTTP timeout for a single-IP quick check before a full scan. */
export const LOCAL_IP_QUICK_PROBE_TIMEOUT_MS = 1000;

/** Minimum interval between debounced background probes for the same device. */
const PROBE_DEBOUNCE_MS = 30_000;

const probeDebouncedAt = new Map<string, number>();

export type RefreshVerifiedLocalIpOptions = {
	/** Skip work if this device was probed recently (for fire-and-forget failure paths). */
	debounced?: boolean;
};

type LocalDeviceInfo = Partial<DeviceInfoG2> & Partial<LocalRequest>;

const pushCandidate = (candidates: string[], ip?: string | null) => {
	if (validate_ip(ip) && !candidates.includes(ip)) {
		candidates.push(ip);
	}
};

/**
 * Builds a deduped, priority-ordered list of LAN IPs to probe for a device.
 * Order: active status (eth → Wi‑Fi) → configured static (eth, sta, sta1) → cached / discovered.
 *
 * @param device - Shelly device record
 * @param status - Optional device status; defaults to `devices_status` store
 * @param settings - Optional device settings; defaults to `devices_settings` store
 * @returns Valid IPv4 addresses to try, highest priority first
 */
export const collect_local_ip_candidates = (
	device: Device,
	status = devices_status.getByRealId(device.id),
	settings = devices_settings.getByRealId(device.id),
): string[] => {
	const candidates: string[] = [];
	const realId = real_id(device.id);

	if (is_gen_2(device)) {
		pushCandidate(candidates, status?.eth?.ip);
		pushCandidate(candidates, status?.wifi?.sta_ip);
		pushCandidate(candidates, settings?.eth?.ip);
		pushCandidate(candidates, settings?.wifi?.sta?.ip);
		pushCandidate(candidates, settings?.wifi?.sta1?.ip);
	} else {
		pushCandidate(candidates, status?.wifi_sta?.ip);
		pushCandidate(candidates, settings?.wifi_sta?.ip);
		pushCandidate(candidates, settings?.wifi_sta1?.ip);
	}

	pushCandidate(candidates, device.ip);
	pushCandidate(candidates, get(allDiscovered)[realId]?.ip);

	return candidates;
};

const normalizeMac = (mac?: string) => mac?.replace(/:/g, '').toLowerCase() ?? '';

/**
 * Checks whether a local Shelly.GetDeviceInfo (or Gen1 /shelly) response belongs to the given device.
 *
 * @param device - Shelly device record
 * @param info - Parsed local device info response
 * @returns `true` when `id` or `mac` matches the device's real id
 */
export const device_matches_local_info = (device: Device, info: LocalDeviceInfo): boolean => {
	const expectedId = real_id(device.id).toLowerCase();
	const responseId = info.id?.toLowerCase();
	if (responseId && responseId === expectedId) return true;

	const mac = normalizeMac(info.mac);
	if (!mac) return false;
	if (mac === expectedId) return true;
	if (mac.length > 6 && mac.slice(-6) === expectedId.slice(-6)) return true;

	return false;
};

/**
 * @param device - Shelly device record
 * @param response - Local HTTP response from GetDeviceInfo or /shelly
 * @returns `true` when the device responds at the probed IP (including 401 auth-locked)
 */
const isReachableAtIp = (device: Device, response: LocalRequest): boolean => {
	if (response?.status === 401) return true;
	return Boolean(response?.isok && device_matches_local_info(device, response));
};

/**
 * Probes one IP via Shelly.GetDeviceInfo.
 *
 * @param device - Shelly device record
 * @param ip - IPv4 address to probe
 * @param timeout - Request timeout in ms
 * @returns The IP when reachable, otherwise `false`
 */
const probe_single_ip = async (device: Device, ip: string, timeout: number): Promise<string | false> => {
	const response = await get_local_device_info(device, ip, timeout);
	return isReachableAtIp(device, response) ? ip : false;
};

/**
 * Probes candidate IPs in parallel and returns the first reachable address in priority order.
 * Resolves as soon as the highest-priority completed candidate succeeds (does not wait for all).
 *
 * @param device - Shelly device record
 * @param candidates - IPs to probe; defaults to `collect_local_ip_candidates(device)`
 * @param options.timeout - Per-request timeout in ms; defaults to {@link LOCAL_IP_PROBE_TIMEOUT_MS}
 * @returns The first responding IP in priority order, or `false` when none respond
 */
export const probe_reachable_local_ip = (
	device: Device,
	candidates = collect_local_ip_candidates(device),
	options?: { timeout?: number },
): Promise<string | false> => {
	if (candidates.length === 0) return Promise.resolve(false);

	const timeout = options?.timeout ?? LOCAL_IP_PROBE_TIMEOUT_MS;

	return new Promise((resolve) => {
		let settled = false;
		const completed = candidates.map(() => false);
		const results: (string | false)[] = candidates.map(() => false);
		let remaining = candidates.length;

		const tryResolve = () => {
			if (settled) return;

			for (let index = 0; index < candidates.length; index++) {
				if (!completed[index]) return;
				if (results[index]) {
					settled = true;
					resolve(results[index]);
					return;
				}
			}

			if (remaining === 0) {
				settled = true;
				resolve(false);
			}
		};

		const onProbeDone = (index: number, result: string | false) => {
			if (settled) return;
			results[index] = result;
			completed[index] = true;
			remaining--;
			tryResolve();
		};

		candidates.forEach((ip, index) => {
			probe_single_ip(device, ip, timeout)
				.then((result) => onProbeDone(index, result))
				.catch(() => onProbeDone(index, false));
		});
	});
};

/**
 * @param device - Shelly device record
 * @returns `true` when local control is enabled and the phone is on the device Wi‑Fi
 */
const can_probe_device = (device: Device): boolean => {
	const networkSSID = get(wifi_ssid);
	const status = devices_status.getByRealId(device.id);
	const settings = devices_settings.getByRealId(device.id);
	const deviceSSID = get_ssid(settings, status, device);
	const useLocal = get(home_data)?.globalLocalControl?.useLocal === true;

	return (
		deviceSSID === networkSSID &&
		useLocal === true &&
		!SLEEPING_TYPES.includes(device?.type) &&
		!HALF_SLEEPING_TYPES.includes(device?.type)
	);
};

/** Persists a newly verified IP on the device record when it differs from the stored value. */
const persistVerifiedIp = (device: Device, ip: string) => {
	const realId = real_id(device.id);
	set_verified_local_ip(realId, ip);
	if (device.ip !== ip) {
		devices.massUpdate({
			[realId]: { ...device, ip },
		});
	}
};

/**
 * Finds a reachable LAN IP and updates the verified cache.
 * 1. Fresh cache → return immediately (no HTTP)
 * 2. Quick single-IP try on sync guess / device.ip
 * 3. Full multi-interface scan with priority early exit
 *
 * Use `{ debounced: true }` with `void` on failure paths so the UI is not blocked.
 *
 * @param deviceId - Device id (channel suffix allowed)
 * @param options.debounced - When true, no-op if probed within the debounce window
 * @returns Verified LAN IP, or `false` when probing is skipped or no IP responds
 */
export const refresh_verified_local_ip = async (
	deviceId: string,
	options?: RefreshVerifiedLocalIpOptions,
): Promise<string | false> => {
	const realId = real_id(deviceId);

	if (options?.debounced) {
		const now = Date.now();
		const lastProbeAt = probeDebouncedAt.get(realId) ?? 0;
		if (now - lastProbeAt < PROBE_DEBOUNCE_MS) return false;
		probeDebouncedAt.set(realId, now);
	}

	const device = get(devices)[realId];
	if (!device || !can_probe_device(device)) return false;

	const cachedIp = get_fresh_verified_local_ip(realId);
	if (cachedIp) return cachedIp;

	const quickCandidates: string[] = [];
	const syncGuess = get_local_ip(deviceId);
	if (typeof syncGuess === 'string') pushCandidate(quickCandidates, syncGuess);
	pushCandidate(quickCandidates, device.ip);

	for (const ip of quickCandidates) {
		const reachable = await probe_single_ip(device, ip, LOCAL_IP_QUICK_PROBE_TIMEOUT_MS);
		if (reachable) {
			persistVerifiedIp(device, reachable);
			return reachable;
		}
	}

	const allCandidates = collect_local_ip_candidates(device);
	const scanCandidates = allCandidates.filter((ip) => !quickCandidates.includes(ip));
	const reachableIp = await probe_reachable_local_ip(
		device,
		scanCandidates.length > 0 ? scanCandidates : allCandidates,
	);

	if (!reachableIp) return false;

	persistVerifiedIp(device, reachableIp);
	return reachableIp;
};

export { clear_verified_local_ips, get_fresh_verified_local_ip };
